import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Pencil, Trash2, Check, X, Menu as MenuIcon, ChevronUp, ChevronDown } from "lucide-react";

const EMPTY_ITEM = { section: "", name: "", description: "", price_inr: 0, is_available: true };

export default function MenuAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ ...EMPTY_ITEM });
  const [editingId, setEditingId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/menu/items")
      .then(r => setItems(r.data || []))
      .catch(e => toast.error(formatApiError(e.response?.data?.detail, "Could not load menu")))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const grouped = useMemo(() => {
    const g = {};
    items.slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .forEach(it => { (g[it.section] = g[it.section] || []).push(it); });
    return g;
  }, [items]);

  const sections = Object.keys(grouped);

  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({ section: it.section, name: it.name, description: it.description, price_inr: it.price_inr, is_available: it.is_available });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => { setEditingId(null); setForm({ ...EMPTY_ITEM }); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.section.trim()) return toast.error("Section is required");
    if (!form.name.trim()) return toast.error("Item name is required");
    const payload = { ...form, section: form.section.trim(), name: form.name.trim(), price_inr: Number(form.price_inr) || 0 };
    try {
      if (editingId) {
        await api.put(`/admin/menu/items/${editingId}`, payload);
        toast.success("Item updated");
      } else {
        // Place new items at the end of their section
        const sectionItems = grouped[payload.section] || [];
        const nextOrder = sectionItems.length ? Math.max(...sectionItems.map(i => i.sort_order || 0)) + 1 : (sections.length * 1000);
        await api.post("/admin/menu/items", { ...payload, sort_order: nextOrder });
        toast.success("Item added");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Save failed"));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/menu/items/${id}`);
      toast.success("Item removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Delete failed"));
    }
  };

  const move = async (secItems, idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= secItems.length) return;
    const reordered = [...secItems];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    // Rebuild the full order preserving section groupings
    const fullOrder = [];
    sections.forEach(s => {
      if (s === reordered[0].section) fullOrder.push(...reordered.map(i => i.id));
      else fullOrder.push(...(grouped[s] || []).map(i => i.id));
    });
    try {
      await api.post("/admin/menu/reorder", { order: fullOrder });
      load();
    } catch (err) {
      toast.error("Reorder failed");
    }
  };

  const toggleAvail = async (it) => {
    try {
      await api.put(`/admin/menu/items/${it.id}`, { is_available: !it.is_available });
      load();
    } catch { toast.error("Could not toggle availability"); }
  };

  return (
    <div data-testid="admin-menu-page">
      <Toaster position="top-center" richColors />
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Cafe</div>
          <h1 className="display text-4xl mt-2 flex items-center gap-3"><MenuIcon size={26} /> Menu.</h1>
          <p className="text-sm text-muted2 mt-2 max-w-xl">
            Everything edited here shows up on the public café page in real time. Toggle an item off to hide it without deleting; reorder within a section using the arrows.
          </p>
        </div>
      </div>

      {/* Add / edit form */}
      <form onSubmit={save} className="mt-8 border border-soft p-5 bg-white" data-testid="menu-form">
        <div className="overline mb-3">{editingId ? "Edit item" : "Add item"}</div>
        <div className="grid md:grid-cols-12 gap-3">
          <label className="md:col-span-3 text-xs">
            <div className="mb-1 text-muted2 uppercase tracking-widest">Section</div>
            <input list="menu-sections" value={form.section} onChange={e => setForm({ ...form, section: e.target.value })}
              className="w-full bg-transparent border-b border-soft py-2 text-sm" placeholder="e.g. BEVERAGES" data-testid="menu-form-section" />
            <datalist id="menu-sections">
              {sections.map(s => <option key={s} value={s} />)}
            </datalist>
          </label>
          <label className="md:col-span-4 text-xs">
            <div className="mb-1 text-muted2 uppercase tracking-widest">Item name</div>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full bg-transparent border-b border-soft py-2 text-sm" placeholder="e.g. Cappuccino" data-testid="menu-form-name" />
          </label>
          <label className="md:col-span-3 text-xs">
            <div className="mb-1 text-muted2 uppercase tracking-widest">Description (optional)</div>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full bg-transparent border-b border-soft py-2 text-sm" placeholder="ingredients, size…" data-testid="menu-form-desc" />
          </label>
          <label className="md:col-span-1 text-xs">
            <div className="mb-1 text-muted2 uppercase tracking-widest">₹</div>
            <input type="number" min={0} value={form.price_inr}
              onChange={e => setForm({ ...form, price_inr: e.target.value })}
              className="w-full bg-transparent border-b border-soft py-2 text-sm font-serif" data-testid="menu-form-price" />
          </label>
          <label className="md:col-span-1 text-xs flex items-end gap-2 pb-2">
            <input type="checkbox" checked={form.is_available} onChange={e => setForm({ ...form, is_available: e.target.checked })} data-testid="menu-form-available" />
            <span>Live</span>
          </label>
        </div>
        <div className="mt-4 flex gap-3">
          <button type="submit" className="btn-primary" data-testid="menu-form-submit">
            {editingId ? <><Check size={14} /> Save changes</> : <><Plus size={14} /> Add item</>}
          </button>
          {editingId && (
            <button type="button" onClick={resetForm} className="btn-ghost" data-testid="menu-form-cancel"><X size={14} /> Cancel</button>
          )}
        </div>
      </form>

      {/* Grouped list */}
      <div className="mt-10 space-y-10">
        {loading && <div className="text-muted2">Loading…</div>}
        {!loading && sections.length === 0 && <div className="text-muted2 text-sm">No items yet — add the first one above.</div>}
        {sections.map(sec => (
          <section key={sec} className="border border-soft bg-white" data-testid={`admin-menu-section-${sec.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="px-4 py-3 border-b border-soft flex items-baseline justify-between">
              <div className="overline">{sec}</div>
              <div className="text-xs text-muted2">{grouped[sec].length} items</div>
            </div>
            <ul>
              {grouped[sec].map((it, idx) => (
                <li key={it.id} className={`px-4 py-3 border-b border-soft flex items-center gap-3 ${!it.is_available ? "opacity-50" : ""}`} data-testid={`admin-menu-row-${it.id}`}>
                  <div className="flex flex-col">
                    <button onClick={() => move(grouped[sec], idx, -1)} disabled={idx === 0} className="disabled:opacity-30 hover:text-terra" data-testid={`row-up-${it.id}`}><ChevronUp size={14} /></button>
                    <button onClick={() => move(grouped[sec], idx, +1)} disabled={idx === grouped[sec].length - 1} className="disabled:opacity-30 hover:text-terra" data-testid={`row-down-${it.id}`}><ChevronDown size={14} /></button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-lg truncate">{it.name}</div>
                    {it.description && <div className="text-xs text-muted2 italic truncate">{it.description}</div>}
                  </div>
                  <div className="font-serif text-lg w-20 text-right">₹{it.price_inr}</div>
                  <button onClick={() => toggleAvail(it)} className="text-xs text-muted2 hover:text-charcoal px-2" data-testid={`row-toggle-${it.id}`}>
                    {it.is_available ? "Live" : "Hidden"}
                  </button>
                  <button onClick={() => openEdit(it)} className="text-muted2 hover:text-charcoal" data-testid={`row-edit-${it.id}`}><Pencil size={14} /></button>
                  <button onClick={() => del(it.id)} className="text-muted2 hover:text-red-700" data-testid={`row-delete-${it.id}`}><Trash2 size={14} /></button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
